import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Move, Send, Sparkles, Volume2, VolumeX, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { sendVipChatMessage, speakKonshusVoice } from "@/lib/api";
import { pickText } from "@/lib/content";
import { KONSHUS_NAME, KONSHUS_VIDEO_URL, KONSHUS_POSTER_URL } from "@/lib/vip";

const EMOTION_COLORS = {
  joy: "from-amber-500/30 to-yellow-500/20",
  sadness: "from-blue-600/30 to-indigo-600/20",
  anxiety: "from-purple-500/30 to-violet-500/20",
  frustration: "from-red-500/30 to-orange-500/20",
  confusion: "from-slate-500/30 to-gray-500/20",
  curiosity: "from-cyan-500/30 to-teal-500/20",
  awe: "from-fuchsia-500/30 to-pink-500/20",
  determination: "from-emerald-500/30 to-green-500/20",
  skepticism: "from-zinc-500/30 to-stone-500/20",
  peace: "from-sky-400/30 to-blue-400/20",
  longing: "from-rose-500/30 to-pink-500/20",
  excitement: "from-orange-500/30 to-amber-500/20",
  neutral: "from-primary/20 to-primary/10",
};

export const FloatingOracle = ({ language, vipEmail, onClose }) => {
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [currentEmotion, setCurrentEmotion] = useState("neutral");
  const [emotionAck, setEmotionAck] = useState("");
  const [isPortalMode, setIsPortalMode] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Dragging handlers
  const handleMouseDown = (e) => {
    if (e.target.closest("[data-no-drag]")) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 300, e.clientY - dragOffset.y)),
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Pop-out to new window
  const handlePopOut = () => {
    const width = 450;
    const height = 650;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popupUrl = `${window.location.origin}/oracle-popup?email=${encodeURIComponent(vipEmail)}`;
    window.open(
      popupUrl,
      "KonshusOracle",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    try {
      setSending(true);
      setReply("");
      setEmotionAck("");

      const response = await sendVipChatMessage({
        email: vipEmail,
        message: message.trim(),
        avatar_style: "oracle",
        detect_emotion: true,
      });

      setReply(response.reply);
      
      // Handle emotion detection
      if (response.detected_emotion) {
        setCurrentEmotion(response.detected_emotion);
        if (response.emotional_acknowledgment) {
          setEmotionAck(response.emotional_acknowledgment);
        }
      } else {
        setCurrentEmotion("neutral");
      }

      // Speak the reply if voice enabled
      if (voiceEnabled && response.reply) {
        try {
          const voiceResponse = await speakKonshusVoice({
            email: vipEmail,
            text: response.reply.substring(0, 500),
            generation_type: "floating-oracle",
          });
          if (voiceResponse.audio_url && audioRef.current) {
            audioRef.current.src = voiceResponse.audio_url;
            audioRef.current.play().catch(() => {});
          }
        } catch (voiceError) {
          console.error("Voice generation failed:", voiceError);
        }
      }

      setMessage("");
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Oracle connection failed.", "Connessione all'oracolo fallita."));
    } finally {
      setSending(false);
    }
  };

  const emotionGradient = EMOTION_COLORS[currentEmotion] || EMOTION_COLORS.neutral;

  if (isMinimized) {
    return (
      <div
        className="fixed z-[9999] cursor-pointer"
        style={{ left: position.x, top: position.y }}
        onClick={() => setIsMinimized(false)}
      >
        <div className={`relative h-20 w-20 rounded-full border-2 border-primary/50 bg-gradient-to-br ${emotionGradient} shadow-2xl shadow-primary/20 transition-all hover:scale-110`}>
          <div className="absolute inset-2 overflow-hidden rounded-full">
            <video
              autoPlay
              className="h-full w-full object-cover"
              loop
              muted
              playsInline
              poster={KONSHUS_POSTER_URL}
              src={KONSHUS_VIDEO_URL}
            />
          </div>
          <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-black">
            <Sparkles className="h-3 w-3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed z-[9999] w-[400px] overflow-hidden rounded-3xl border border-primary/30 bg-[#0a0a0e]/95 shadow-2xl shadow-black/50 backdrop-blur-xl"
      data-testid="floating-oracle"
      style={{ left: position.x, top: position.y }}
    >
      {/* Header - Draggable */}
      <div
        className="flex cursor-move items-center justify-between border-b border-white/10 bg-black/50 px-4 py-3"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <Move className="h-4 w-4 text-slate-500" />
          <span className="text-xs uppercase tracking-[0.2em] text-primary">{KONSHUS_NAME}</span>
          {currentEmotion !== "neutral" && (
            <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] text-primary">
              {currentEmotion}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1" data-no-drag>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={() => setIsPortalMode(!isPortalMode)}
            title={isPortalMode ? "Avatar Mode" : "Portal Mode"}
          >
            <Sparkles className="h-4 w-4" />
          </button>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            title={voiceEnabled ? "Mute" : "Unmute"}
          >
            {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={handlePopOut}
            title="Pop out"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            onClick={() => setIsMinimized(true)}
            title="Minimize"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-red-400"
            onClick={onClose}
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Avatar/Portal Display */}
      <div className={`relative h-48 overflow-hidden bg-gradient-to-b ${emotionGradient}`}>
        {isPortalMode ? (
          /* Mystical Portal Effect */
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Outer rings */}
              <div className="absolute -inset-8 animate-spin rounded-full border border-primary/20" style={{ animationDuration: "20s" }} />
              <div className="absolute -inset-12 animate-spin rounded-full border border-primary/10" style={{ animationDuration: "30s", animationDirection: "reverse" }} />
              <div className="absolute -inset-16 animate-spin rounded-full border border-dashed border-primary/5" style={{ animationDuration: "40s" }} />
              
              {/* Inner portal */}
              <div className="relative h-32 w-32 overflow-hidden rounded-full border-2 border-primary/50 shadow-[0_0_60px_rgba(212,175,55,0.3)]">
                <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-primary/30 via-transparent to-primary/20" />
                <video
                  autoPlay
                  className="h-full w-full object-cover opacity-80"
                  loop
                  muted
                  playsInline
                  poster={KONSHUS_POSTER_URL}
                  ref={videoRef}
                  src={KONSHUS_VIDEO_URL}
                />
                {/* Mystical overlay */}
                <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_30%,rgba(10,10,14,0.8)_100%)]" />
              </div>
              
              {/* Glowing center */}
              <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/80 blur-sm" />
            </div>
          </div>
        ) : (
          /* Full Avatar Mode */
          <div className="relative h-full w-full">
            <video
              autoPlay
              className="h-full w-full object-cover"
              loop
              muted
              playsInline
              poster={KONSHUS_POSTER_URL}
              ref={videoRef}
              src={KONSHUS_VIDEO_URL}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0e] via-transparent to-transparent" />
          </div>
        )}
        
        {/* Emotion indicator glow */}
        {currentEmotion !== "neutral" && (
          <div className={`absolute inset-0 animate-pulse bg-gradient-to-t ${emotionGradient} opacity-50`} />
        )}
      </div>

      {/* Response Area */}
      <div className="max-h-40 overflow-y-auto p-4">
        {emotionAck && (
          <p className="mb-2 text-xs italic text-primary/80" data-testid="emotion-acknowledgment">
            {emotionAck}
          </p>
        )}
        {reply ? (
          <p className="text-sm leading-relaxed text-slate-200" data-testid="oracle-reply">
            {reply}
          </p>
        ) : (
          <p className="text-center text-xs text-slate-500">
            {pickText(language, "Ask the oracle anything...", "Chiedi qualsiasi cosa all'oracolo...")}
          </p>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-white/10 p-3" data-no-drag>
        <div className="flex gap-2">
          <Input
            className="h-10 flex-1 border-white/10 bg-black/30 text-sm text-white placeholder:text-slate-500"
            data-testid="floating-oracle-input"
            disabled={sending}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder={pickText(language, "Speak to Kōnshus...", "Parla con Kōnshus...")}
            value={message}
          />
          <Button
            className="h-10 w-10 rounded-full bg-primary p-0 text-black hover:bg-primary/80"
            data-testid="floating-oracle-send"
            disabled={sending || !message.trim()}
            onClick={handleSend}
          >
            {sending ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} />
    </div>
  );
};
